import { useState } from 'react';
import { textoDoValor } from '@/pages/evento/utils';
import { basePathSite, urlEventoComCodigo } from '@/pages/assistente-evento/utils';
import type { LinkDivulgar } from '../types';

interface Props {
  eventoNome: string;
  slug: string;
  links: LinkDivulgar[];
  vendeIngressos: boolean;
}

function formatarAbertura(iso: string): string {
  const d = new Date(iso);
  const dia = String(d.getDate()).padStart(2, '0');
  const mes = String(d.getMonth() + 1).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${dia}/${mes} ${hh}:${mm}`;
}

function situacaoTexto(l: LinkDivulgar, vendeIngressos: boolean): string {
  const palavra = vendeIngressos ? 'ingressos' : 'inscrições';
  switch (l.situacao) {
    case 'abertas':
      return `${palavra} abertas`;
    case 'em_breve':
      return l.inscricoes_abrem_em ? `abre ${formatarAbertura(l.inscricoes_abrem_em)}` : 'em breve';
    case 'encerradas':
      return 'encerradas';
    case 'esgotado':
      return 'esgotado';
    default:
      return 'indisponível';
  }
}

function urlInscricao(id: number): string {
  return `${window.location.origin}${basePathSite()}/inscricao/${id}`;
}

export default function LinksDivulgar({ eventoNome, slug, links, vendeIngressos }: Props) {
  const [copiadoId, setCopiadoId] = useState<number | null>(null);

  if (links.length === 0) return null;

  async function copiar(id: number, url: string) {
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      /* falha silenciosa: o botão ainda mostra o feedback */
    }
    setCopiadoId(id);
    setTimeout(() => {
      setCopiadoId((atual) => (atual === id ? null : atual));
    }, 2000);
  }

  function enviarWhatsApp(l: LinkDivulgar, url: string) {
    const palavra = vendeIngressos ? 'Ingressos' : 'Inscrições';
    const texto = encodeURIComponent(
      `${palavra} abertos para ${eventoNome} — ${l.nome}. ${vendeIngressos ? 'Compre o seu' : 'Faça a sua'}: ${url}`,
    );
    window.open(`https://wa.me/?text=${texto}`, '_blank', 'noopener,noreferrer');
  }

  return (
    <div className="overflow-hidden rounded-lg border border-secondary-200 bg-white">
      <div className="px-5 py-4">
        <h2 className="text-base font-medium text-foreground-950">Links para divulgar</h2>
        <p className="mt-0.5 text-sm text-secondary-500">
          {vendeIngressos
            ? 'Copie o link de cada ingresso e envie nos grupos.'
            : 'Copie o link de cada participação e envie nos grupos.'}
        </p>
      </div>

      <div className="divide-y divide-secondary-100 border-t border-secondary-100">
        {links.map((l) => {
          const url = l.publica ? urlInscricao(l.id) : urlEventoComCodigo(slug, l.codigo || '');
          const copiado = copiadoId === l.id;
          return (
            <div key={l.id} className="px-5 py-4">
              <div className="flex flex-col gap-1.5 sm:flex-row sm:items-center sm:justify-between">
                <span className="text-[15px] font-medium text-foreground-950">{l.nome}</span>
                <span className="text-sm text-secondary-500">
                  {textoDoValor({ valor_situacao: l.valor_situacao, valor_centavos: l.valor_centavos })} ·{' '}
                  {situacaoTexto(l, vendeIngressos)}
                </span>
              </div>

              <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <code className="min-w-0 truncate font-mono text-xs text-foreground-800">
                  {url.replace(/^https?:\/\//, '')}
                </code>
                <div className="flex shrink-0 items-center gap-2">
                  <button
                    type="button"
                    onClick={() => enviarWhatsApp(l, url)}
                    className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-secondary-300 bg-white px-3 py-1.5 text-sm font-medium text-foreground-800 transition-colors hover:bg-background-100"
                  >
                    <span className="flex h-4 w-4 items-center justify-center text-green-600">
                      <i className="ri-whatsapp-line" />
                    </span>
                    Enviar no WhatsApp
                  </button>
                  <button
                    type="button"
                    onClick={() => copiar(l.id, url)}
                    className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-primary-600"
                  >
                    <span className="flex h-4 w-4 items-center justify-center">
                      <i className={copiado ? 'ri-check-line' : 'ri-file-copy-line'} />
                    </span>
                    {copiado ? 'Copiado ✓' : 'Copiar'}
                  </button>
                </div>
              </div>

              {!l.publica && (
                <p className="mt-1.5 text-xs text-secondary-500">
                  ↳ privada: só quem tem o link entra
                </p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}